(function() {
  'use strict';

  const SELECTORS = {
    CART_DRAWER: '#CartDrawer',
    COUPON_FORM: '#discount-coupon-form',
    COUPON_INPUT: '#discount-coupon-input',
    COUPON_BUTTON: '.discount-coupon__button',
    COUPON_LIST: '.discount-coupon__list',
    COUPON_MESSAGE: '.discount-coupon__message',
    COUPON_DETAILS: '.discount-coupon',
    DRAWER_TOTAL: '#CartDrawer .totals__total-value',
    PAGE_TOTAL: '.totals__total-value',
    CART_FOOTER: '#main-cart-footer, .cart__footer',
    FREE_SHIPPING_BAR: 'free-shipping-progress-bar'
  };

  const API_ENDPOINTS = {
    CART: '/cart.js',
    CART_UPDATE: '/cart/update.js'
  };

  const MESSAGES = {
    EMPTY_CODE: 'Insira um código de desconto válido',
    DUPLICATE_CODE: 'Este cupom já foi aplicado',
    NOT_APPLICABLE: (code, itemCount) => {
      const itemText = itemCount === 1 ? 'o item' : 'os itens';
      return `O código de desconto ${code} não é válido para ${itemText} do carrinho`;
    },
    REMOVED: 'Cupom removido',
    ERROR_APPLY: 'Erro ao aplicar o cupom',
    ERROR_REMOVE: 'Erro ao remover o cupom'
  };

  const HTTP_HEADERS = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  const TIMINGS = {
    MESSAGE_DISPLAY: 4000,
    CART_UPDATE_DELAY: 100,
    OBSERVER_DEBOUNCE: 800,
    INIT_RETRY: 100
  };

  let isInitialized = false;

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(cents / 100);
  };

  const waitForNextFrame = () => {
    return new Promise(resolve => requestAnimationFrame(resolve));
  };

  const isCartPage = () => window.location.pathname === '/cart';
  
  const hasCartDrawer = () => !!document.querySelector(SELECTORS.CART_DRAWER);

  const CartAPI = {
    async get() {
      const response = await fetch(API_ENDPOINTS.CART);
      return await response.json();
    },

    async updateDiscounts(discountCodes) {
      await fetch(API_ENDPOINTS.CART_UPDATE, {
        method: 'POST',
        headers: HTTP_HEADERS,
        body: JSON.stringify({ discount: discountCodes })
      });
    },

    async clearDiscounts() {
      await this.updateDiscounts('');
    },

    async getCurrentCoupons() {
      try {
        const data = await this.get();
        const discountCodes = data.discount_codes || [];
        return discountCodes.map(dc => dc.code.toUpperCase());
      } catch (error) {
        console.error('Error getting current coupons:', error);
        return [];
      }
    },

    async getApplicableCoupons() {
      try {
        const data = await this.get();
        const discountCodes = data.discount_codes || [];
        return discountCodes
          .filter(dc => dc.applicable)
          .map(dc => dc.code.toUpperCase());
      } catch (error) {
        console.error('Error getting applicable coupons:', error);
        return [];
      }
    }
  };

  const UI = {
    getFormElements() {
      const form = document.getElementById(SELECTORS.COUPON_FORM.slice(1));
      const input = document.getElementById(SELECTORS.COUPON_INPUT.slice(1));
      const button = form?.querySelector(SELECTORS.COUPON_BUTTON);
      return { form, input, button };
    },

    showMessage(message, type = 'error') {
      const { form } = this.getFormElements();
      const messageContainer = form?.parentElement.querySelector(SELECTORS.COUPON_MESSAGE);
      if (!messageContainer) return;

      const paragraph = messageContainer.querySelector('.form__message');
      paragraph.textContent = message;
      paragraph.style.color = type === 'error' ? 'red' : 
                              type === 'success' ? 'green' : 'inherit';
      messageContainer.style.display = 'block';
      
      setTimeout(() => {
        messageContainer.style.display = 'none';
      }, TIMINGS.MESSAGE_DISPLAY);
    },

    setButtonLoading(button, isLoading) {
      button.disabled = isLoading;
      button.classList.toggle('loading', isLoading);
    },

    createCouponBadge(code, onRemove) {
      const container = document.createElement('div');
      container.className = 'flex items-center gap-x-2 cart-coupon-applied';
      container.setAttribute('data-discount-code', code);
      
      const text = document.createElement('span');
      text.className = 'cart-coupon-applied-text';
      text.textContent = code;
      
      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'cart-coupon-remove';
      removeButton.setAttribute('aria-label', 'Remover cupom');
      removeButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="var(--icon-stroke-width)" stroke="currentColor" class="icon icon-close size-10">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
      </svg>`;
      
      removeButton.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setButtonLoading(removeButton, true);
        
        try {
          await onRemove(code);
        } finally {
          this.setButtonLoading(removeButton, false);
        }
      });
      
      container.appendChild(text);
      container.appendChild(removeButton);
      
      return container;
    },

    updateCouponList(codes, onRemove) {
      const list = document.querySelector(SELECTORS.COUPON_LIST);
      if (!list) return;

      list.innerHTML = '';
      codes.forEach(code => {
        const badge = this.createCouponBadge(code, onRemove);
        list.appendChild(badge);
      });
    },

    updateTotalPrices(totalPrice) {
      const formattedPrice = formatCurrency(totalPrice);

      const drawerTotal = document.querySelector(SELECTORS.DRAWER_TOTAL);
      if (drawerTotal) {
        drawerTotal.textContent = formattedPrice;
      }

      const pageTotals = document.querySelectorAll(SELECTORS.PAGE_TOTAL);
      pageTotals.forEach(element => {
        if (element.closest(SELECTORS.CART_FOOTER)) {
          element.textContent = formattedPrice;
        }
      });
    },

    updateFreeShippingBar(cartData) {
      const bars = document.querySelectorAll(SELECTORS.FREE_SHIPPING_BAR);
      bars.forEach(async (bar) => {
        if (bar && bar.fetchCartData) {
          await bar.fetchCartData();
          bar.dataset.cartTotal = cartData.total_price;
          bar.updateStatus();
        }
      });
    }
  };

  const CouponOperations = {
    async apply() {
      const { form, input, button } = UI.getFormElements();
      
      if (!form || !input || !button) {
        console.error('Coupon form elements not found');
        return;
      }

      const code = input.value.trim().toUpperCase();
      
      if (!code) {
        UI.showMessage(MESSAGES.EMPTY_CODE, 'error');
        return;
      }

      const existingCoupons = await CartAPI.getCurrentCoupons();
      if (existingCoupons.includes(code)) {
        UI.showMessage(MESSAGES.DUPLICATE_CODE, 'error');
        input.value = '';
        return;
      }

      UI.setButtonLoading(button, true);

      try {
        const previousApplicable = await CartAPI.getApplicableCoupons();
        const allCoupons = [...existingCoupons, code];

        await CartAPI.clearDiscounts();
        await CartAPI.updateDiscounts(allCoupons.join(','));

        const cartData = await CartAPI.get();
        const validationResult = this.validateCouponApplication(
          code, 
          cartData.discount_codes || [],
          cartData.item_count || 0
        );

        if (!validationResult.success) {
          UI.showMessage(validationResult.message, 'error');
          input.value = '';
          await this.restorePreviousCoupons(previousApplicable);
          await this.refresh();
          return;
        }

        input.value = '';
        await this.refresh();
        await PageReload.reloadIfNeeded();
        document.dispatchEvent(new CustomEvent('cart:updated'));

      } catch (error) {
        console.error('Error applying coupon:', error);
        UI.showMessage(MESSAGES.ERROR_APPLY, 'error');
      } finally {
        UI.setButtonLoading(button, false);
      }
    },

    validateCouponApplication(code, discountCodes, itemCount) {
      const newCoupon = discountCodes.find(
        dc => dc.code.toUpperCase() === code.toUpperCase()
      );
      
      if (!newCoupon || !newCoupon.applicable) {
        return {
          success: false,
          message: MESSAGES.NOT_APPLICABLE(code, itemCount)
        };
      }

      return { success: true };
    },

    async restorePreviousCoupons(previousApplicable) {
      await CartAPI.clearDiscounts();
      if (previousApplicable.length > 0) {
        await CartAPI.updateDiscounts(previousApplicable.join(','));
      }
    },

    async remove(couponCode) {
      if (!couponCode) return;

      try {
        const currentCoupons = await CartAPI.getCurrentCoupons();
        const remainingCoupons = currentCoupons.filter(
          code => code.trim().toUpperCase() !== couponCode.trim().toUpperCase()
        );

        await CartAPI.clearDiscounts();
        
        if (remainingCoupons.length > 0) {
          await CartAPI.updateDiscounts(remainingCoupons.join(','));
        }

        UI.showMessage(MESSAGES.REMOVED, 'success');
        
        setTimeout(async () => {
          await this.refresh();
          await PageReload.reloadIfNeeded();
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }, 500);

      } catch (error) {
        console.error('Error removing coupon:', error);
        UI.showMessage(MESSAGES.ERROR_REMOVE, 'error');
      }
    },

    async refresh() {
      try {
        const cartData = await CartAPI.get();
        
        if (cartData.item_count === 0) {
          await CartAPI.clearDiscounts();
          return;
        }

        const applicableCodes = (cartData.discount_codes || [])
          .filter(dc => dc.applicable)
          .map(dc => dc.code.toUpperCase());

        UI.updateCouponList(applicableCodes, (code) => this.remove(code));
        
        UI.updateTotalPrices(cartData.total_price);
        
        UI.updateFreeShippingBar(cartData);

        await this.cleanNonApplicableCoupons(cartData);

      } catch (error) {
        console.error('Error refreshing coupons:', error);
      }
    },

    async cleanNonApplicableCoupons(cartData) {
      const discountCodes = cartData.discount_codes || [];
      const applicableCodes = discountCodes
        .filter(dc => dc.applicable)
        .map(dc => dc.code.toUpperCase());
      const allCodes = discountCodes.map(dc => dc.code.toUpperCase());

      if (applicableCodes.length !== allCodes.length) {
        await CartAPI.updateDiscounts(applicableCodes.join(','));
      }
    }
  };

  const PageReload = {
    async reloadIfNeeded() {
      if (hasCartDrawer()) {
        await this.reloadDrawer();
      }
      if (isCartPage()) {
        await this.reloadPage();
      }
    },

    async reloadDrawer() {
      try {
        const drawer = document.querySelector(SELECTORS.CART_DRAWER);
        if (!drawer) return;

        const couponDetails = drawer.querySelector(SELECTORS.COUPON_DETAILS);
        const wasOpen = couponDetails?.hasAttribute('open');
        
        const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
        const html = await response.text();
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const newDrawer = tempDiv.querySelector(SELECTORS.CART_DRAWER);
        
        if (newDrawer) {
          drawer.innerHTML = newDrawer.innerHTML;
          
          if (wasOpen) {
            const restoredDetails = drawer.querySelector(SELECTORS.COUPON_DETAILS);
            restoredDetails?.setAttribute('open', '');
          }

          await waitForNextFrame();
          await CouponOperations.refresh();
          window.initDiscountCoupon?.();
        }

        const cartData = await CartAPI.get();
        if (cartData.item_count === 0) {
          await CartAPI.clearDiscounts();
        }
      } catch (error) {
        console.error('Error reloading drawer:', error);
      }
    },

    async reloadPage() {
      if (!isCartPage()) return;

      const sectionsToReload = [
        { id: 'main-cart-items', selector: '.js-contents' },
        { id: 'main-cart-footer', selector: '.js-contents' }
      ];
      
      for (const { id, selector } of sectionsToReload) {
        try {
          const section = document.getElementById(id);
          if (!section) continue;

          const sectionId = section.dataset.id;
          if (!sectionId) continue;

          const wasOpen = section.querySelector('.discount-coupon details')?.hasAttribute('open');
          
          const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
          const html = await response.text();
          
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const newSection = tempDiv.querySelector(`#${id}`);
          
          if (newSection) {
            const elementToReplace = section.querySelector(selector) || section;
            const newElement = newSection.querySelector(selector) || newSection;
            
            if (elementToReplace && newElement) {
              elementToReplace.innerHTML = newElement.innerHTML;
            } else {
              section.innerHTML = newSection.innerHTML;
            }
            
            if (wasOpen) {
              const restoredDetails = section.querySelector('.discount-coupon details');
              restoredDetails?.setAttribute('open', '');
            }

            await waitForNextFrame();
            await CouponOperations.refresh();
            window.initDiscountCoupon?.();
          }
        } catch (error) {
          console.error(`Error reloading section ${id}:`, error);
        }
      }
    }
  };

  const EventManager = {
    setupGlobalListeners() {
      if (isInitialized) return;
      isInitialized = true;

      document.addEventListener('submit', (e) => {
        if (e.target?.id === SELECTORS.COUPON_FORM.slice(1)) {
          e.preventDefault();
          e.stopPropagation();
          CouponOperations.apply();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.target?.id === SELECTORS.COUPON_INPUT.slice(1) && e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          CouponOperations.apply();
        }
      });

      document.addEventListener('click', (e) => {
        const button = e.target?.closest(SELECTORS.COUPON_BUTTON);
        if (button) {
          const form = document.getElementById(SELECTORS.COUPON_FORM.slice(1));
          if (form?.contains(button)) {
            e.preventDefault();
            e.stopPropagation();
            CouponOperations.apply();
          }
        }
      });

      document.addEventListener('cart:updated', () => {
        setTimeout(() => {
          CouponOperations.refresh();
        }, TIMINGS.CART_UPDATE_DELAY);
      });
    },

    setupDrawerObserver() {
      const drawer = document.querySelector(SELECTORS.CART_DRAWER);
      if (!drawer) return;

      let observer = null;
      let debounceTimeout = null;

      if (observer) {
        observer.disconnect();
      }

      observer = new MutationObserver((mutations) => {
        const couponListChanged = mutations.some(mutation =>
          Array.from(mutation.addedNodes).some(node =>
            node.classList?.contains('discount-coupon__list')
          )
        );
        
        if (!couponListChanged) return;

        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => {
          CouponOperations.refresh();
        }, TIMINGS.OBSERVER_DEBOUNCE);
      });

      observer.observe(drawer, {
        childList: true,
        subtree: true
      });

      return observer;
    },

    setupPageRestoreListeners() {
      const syncState = async () => {
        try {
          const cartData = await CartAPI.get();
          const applicableCodes = (cartData.discount_codes || [])
            .filter(dc => dc.applicable)
            .map(dc => dc.code.toUpperCase());

          UI.updateCouponList(applicableCodes, (code) => CouponOperations.remove(code));
          UI.updateTotalPrices(cartData.total_price);
        } catch (error) {
          console.error('Error syncing coupon state:', error);
        }
      };

      window.addEventListener('pageshow', syncState);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          syncState();
        }
      });
      window.addEventListener('focus', syncState);
      window.addEventListener('popstate', syncState);
    }
  };

  function initCouponScript() {
    EventManager.setupGlobalListeners();
    EventManager.setupDrawerObserver();
    
    CouponOperations.refresh();

    const bodyObserver = new MutationObserver(() => {
      EventManager.setupDrawerObserver();
    });
    bodyObserver.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
  }

  function tryInit() {
    const form = document.getElementById(SELECTORS.COUPON_FORM.slice(1));
    const container = document.querySelector('.discount-coupon__container');
    
    if (form || container) {
      initCouponScript();
    } else {
      setTimeout(tryInit, TIMINGS.INIT_RETRY);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInit);
  } else {
    tryInit();
  }

  EventManager.setupPageRestoreListeners();

  window.initDiscountCoupon = initCouponScript;
})();
